import { useEffect, useMemo, useState } from 'react';
import { Input, Modal, Tag } from 'antd';
import styled from 'styled-components';

interface IProps {
  isOpen: boolean;
  /** The section title being added (shown in the modal header). */
  sectionTitle: string;
  /** Quick-fill suggestions for this key (e.g. outer / immediate). */
  presets: string[];
  /** Nature/types already used by live sibling instances of the same key. */
  takenNatureTypes: string[];
  /** Pre-filled value — the current nature/type when editing an existing block. */
  initialValue?: string;
  /** Adding a new block (default) or relabelling an existing one. */
  mode?: 'add' | 'edit';
  onConfirm: (natureType: string) => void;
  onCancel: () => void;
}

const normalize = (s: string) => s.trim().toLowerCase();

const NatureTypeModal: React.FC<IProps> = ({
  isOpen,
  sectionTitle,
  presets,
  takenNatureTypes,
  initialValue,
  mode = 'add',
  onConfirm,
  onCancel,
}) => {
  const [value, setValue] = useState(initialValue ?? '');

  useEffect(() => {
    if (isOpen) setValue(initialValue ?? '');
  }, [isOpen, initialValue]);

  const takenSet = useMemo(
    () => new Set(takenNatureTypes.map(normalize)),
    [takenNatureTypes]
  );

  const trimmed = value.trim();
  const isEmpty = trimmed.length === 0;
  const isDuplicate = !isEmpty && takenSet.has(normalize(trimmed));
  const valid = !isEmpty && !isDuplicate;

  return (
    <Modal
      open={isOpen}
      title={
        mode === 'edit'
          ? `Change nature/type: ${sectionTitle}`
          : `Add: ${sectionTitle}`
      }
      okText={mode === 'edit' ? 'Save' : 'Add'}
      cancelText="Cancel"
      okButtonProps={{ disabled: !valid }}
      onOk={() => valid && onConfirm(trimmed)}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Description>
        Enter the packaging nature/type — what gets printed under the heading.
        Instances of the same section must each have a different one.
      </Description>

      {presets.length > 0 && (
        <Presets>
          {presets.map(p => {
            const used = takenSet.has(normalize(p));
            return (
              <Tag.CheckableTag
                key={p}
                checked={normalize(value) === normalize(p)}
                onChange={() => !used && setValue(p)}
                style={
                  used
                    ? { opacity: 0.4, cursor: 'not-allowed' }
                    : { cursor: 'pointer' }
                }
              >
                {p}
              </Tag.CheckableTag>
            );
          })}
        </Presets>
      )}

      <Input
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Packaging nature/type"
        status={isDuplicate ? 'error' : undefined}
        onPressEnter={() => valid && onConfirm(trimmed)}
        autoFocus
      />
      {isDuplicate && (
        <Hint>A block with this nature/type already exists.</Hint>
      )}
    </Modal>
  );
};

export default NatureTypeModal;

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #4b576a;
`;

const Presets = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

const Hint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #cf1322;
`;
